// @flow
import {Unveil} from '@homeaway/react-unveil';
import gql from 'graphql-tag';
import {debounce} from 'lodash';
import React from 'react';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import HitInfo, {fragments as hitInfoFragments} from '../HitInfo/HitInfo';
import classNames from 'classnames';
// $FlowFixMe
import TripBoardButton from '../TripBoardButton/TripBoardButton';
// $FlowFixMe
import TripBoardConfirmation from '../TripBoardButton/TripBoardConfirmation';
import {PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';
// $FlowFixMe
import {VirtualTourBadge} from '../VirtualTourBadge/VirtualTourBadge';

import type {HitEventDataListingShape} from '../../utils/hitEdapUtils';
import type {ListingShape as HitInfoListingShape} from '../HitInfo/HitInfo';
import {Link} from '@homeaway/react-localization-components';

import './Hit.less';
export const fragments = {
    listing: gql`
        fragment HitListing on Listing {
            virtualTourBadge {
                name
                id
                helpText
            }
            amenitiesBadges {
                name
                id
                helpText
            }
            multiUnitProperty
            imageCount
            images {
                altText
                c6_uri
                c9_uri
            }
            listingNamespace
            ...HitInfoListing
        }
        ${hitInfoFragments.listing}
    `
};

export type BadgeShape = {
    id: string,
    name: string,
    helpText: ?string
};

export type ListingImage = {
    altText: ?string,
    c6_uri: ?string,
    c9_uri: ?string
};

export type ListingShape = HitInfoListingShape & {
    availableForDates: ?boolean,
    virtualTourBadge: ?BadgeShape,
    multiUnitProperty: ?boolean,
    images: ?(ListingImage[]),
    imageCount: ?number,
    hitIndex?: number,
    page?: number,
    pageSize?: number
};

export type Props = {
    hitIndex: number,
    index: number,
    isGoodBot?: boolean,
    isWithoutAnchor?: boolean,
    listing: ListingShape,
    onHitClick?: (HitEventDataListingShape) => void,
    onMouseEnter?: (listingId: string) => void,
    onMouseLeave?: (listingId: string) => void,
    page?: number,
    pageSize?: number,
    tripBoardConfirmationTestIsOn?: boolean,
    tripBoardConfirmationIsExpanded?: boolean
};

const HIT_UNVEIL_THRESHOLD = 100;
const HIT_UNVEIL_MIN_HEIGHT = '232px';

function renderHit(props: Props) {
    const {
        onHitClick,
        onMouseEnter,
        onMouseLeave,
        index,
        listing,
        page,
        pageSize,
        tripBoardConfirmationTestIsOn,
        tripBoardConfirmationIsExpanded
    } = props;

    const handleMouseEnter = () =>
        listing && onMouseEnter && onMouseEnter(listing.listingId);
    const handleMouseLeave = () =>
        listing && onMouseLeave && onMouseLeave(listing.listingId);
    const onClick = () =>
        listing &&
        onHitClick &&
        onHitClick({listing, hitIndex: index, page, pageSize});
    const onMouseTimeout = 100;

    const hitClasses = classNames(
        'Hit',
        'media-flex',
        'media-flex--left',
        'media-flex--xs',
        {
            /** @abacus `ha_pulse_save_confirmation_serp_hit` */
            'with-trip-board-confirmation': tripBoardConfirmationTestIsOn,
            'with-trip-board-confirmation--expanded': tripBoardConfirmationIsExpanded
        }
    );

    return (
        listing && (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
                className={hitClasses}
                data-wdio="hit"
                role="listitem"
                onMouseEnter={debounce(handleMouseEnter, onMouseTimeout)}
                onMouseLeave={debounce(handleMouseLeave, onMouseTimeout)}
            >
                <div className="HitCarousel Hit__thumbnail">
                    <span aria-hidden="true">
                        {listing.virtualTourBadge && (
                            <VirtualTourBadge
                                badge={listing.virtualTourBadge}
                                detailPageUrl={listing.detailPageUrl}
                                onBadgeClick={onClick}
                            />
                        )}
                        <div className="Hit__tripboardButton">
                            <TripBoardButton listing={listing} />
                        </div>
                        <HitCarouselContainer
                            detailPageUrl={listing.detailPageUrl}
                            edapAction={PHOTOCAROUSEL_ADVANCE}
                            images={listing.images || []}
                            listingId={listing.listingId}
                            totalImageCount={listing.imageCount || 0}
                            onImageClick={onClick}
                        />
                    </span>
                </div>
                <Link
                    target={'_blank'}
                    className="media-flex__content"
                    href={listing.detailPageUrl}
                    onClick={onClick}
                    tabIndex="0"
                >
                    <HitInfo listing={listing} hitIndex={index} />
                </Link>
                {/* @abacus `ha_pulse_save_confirmation_serp_hit` */}
                {tripBoardConfirmationTestIsOn && (
                    <TripBoardConfirmation
                        actionLocation={'search_listing'}
                        listing={listing}
                    />
                )}
            </div>
        )
    );
}

export default function Hit(props: Props) {
    // Good Bots (deviceClassification = -25) get Hits with no Unveil
    if (props.isGoodBot) {
        return renderHit(props);
    }

    // Unveil for the masses
    return (
        <Unveil
            threshold={HIT_UNVEIL_THRESHOLD}
            minContainerHeight={HIT_UNVEIL_MIN_HEIGHT}
        >
            {renderHit(props)}
        </Unveil>
    );
}
